import React, { useState, useEffect, useContext } from 'react'
import { Form, Button, Row, Col, Alert, Card, Image } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Loader from '../components/Loader.js'
import { updateUserProfile, getUserDetails } from '../actions/userActions.js'
import { USER_UPDATE_PROFILE_RESET } from '../constants/userConstants.js'
import { ThemeContext } from '../components/ThemeContext.js'

// ProfileScreen component
const ProfileScreen = () => {
  const { theme } = useContext(ThemeContext)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const userDetails = useSelector((state) => state.userDetails)
  const { loading, error, user } = userDetails

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const id = userInfo ? userInfo._id : null

  const userUpdateProfile = useSelector((state) => state.userUpdateProfile)
  const { success } = userUpdateProfile

  useEffect(() => {
    if (!userInfo) {
      dispatch({ type: USER_UPDATE_PROFILE_RESET })
      navigate('/login')
    } else {
      if (!user || !user.name || success) {
        dispatch({ type: USER_UPDATE_PROFILE_RESET })
        dispatch(getUserDetails(id))
      }
    }
  }, [dispatch, id, user, success, navigate, userInfo])

  const submitHandler = (e) => {
    e.preventDefault()
    setMessage(null)

    if (password === '') {
      setMessage('Password cannot be empty')
      return
    }

    if (password !== confirmPassword) {
      setMessage('Passwords do not match')
      setPassword('')
      setConfirmPassword('')
      return
    }

    setMessage('Password Updated Successfully')
    dispatch(updateUserProfile({ id, password }))
    setPassword('')
    setConfirmPassword('')
  }

  const formatLastLogin = (dateStr, ipinfo) => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    const location =
      ipinfo?.ipcity && ipinfo?.ipcountry
        ? ` — ${ipinfo.ipcity}, ${ipinfo.ipcountry}`
        : ''
    return `${date}${location}`
  }

  const qrSrc = user?.qrfile ? `/qrcode/${user.qrfile}` : null

  return (
    <Row className='g-4' style={{ color: theme.text }}>
      {/* Left column — account info + QR */}
      <Col md={4}>
        <Card
          className='mb-3'
          style={{
            backgroundColor: theme.cardBackground || theme.background,
            border: `1px solid ${theme.accent}`
          }}
        >
          <Card.Body>
            <Card.Title style={{ color: theme.accent }}>
              Account Info
            </Card.Title>
            <dl
              className='mb-0'
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '4px 12px'
              }}
            >
              <dt>Name</dt>
              <dd>{user?.name || '—'}</dd>
              <dt>Email</dt>
              <dd>{user?.email || '—'}</dd>
              <dt>Phone</dt>
              <dd>{user?.phone || '—'}</dd>
            </dl>
          </Card.Body>
        </Card>

        <Card
          className='mb-3'
          style={{
            backgroundColor: theme.cardBackground || theme.background,
            border: `1px solid ${theme.accent}`
          }}
        >
          <Card.Body>
            <Card.Title style={{ color: theme.accent }}>Address</Card.Title>
            {loading ? (
              <Loader />
            ) : error ? (
              <Alert variant='danger'>{error}</Alert>
            ) : (
              <address className='mb-0'>
                {user?.address}
                <br />
                {user?.city}
                {user?.state ? `, ${user.state}` : ''} {user?.mailcode}
                <br />
                {user?.country}
              </address>
            )}
          </Card.Body>
        </Card>

        <Card
          style={{
            backgroundColor: theme.cardBackground || theme.background,
            border: `1px solid ${theme.accent}`
          }}
        >
          <Card.Body className='text-center'>
            <Card.Title style={{ color: theme.accent }}>My QR Code</Card.Title>
            {qrSrc ? (
              <Image
                src={qrSrc}
                alt='Your QR Code'
                fluid
                style={{ maxWidth: '160px' }}
              />
            ) : (
              <p className='text-muted'>Not available</p>
            )}
            <p
              className='mt-2 mb-0'
              style={{ fontSize: '0.75rem', color: theme.text }}
            >
              Last login: {formatLastLogin(user?.lastLogin, user?.lastloginip)}
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Middle column — change password */}
      <Col md={4}>
        <Card
          style={{
            backgroundColor: theme.cardBackground || theme.background,
            border: `1px solid ${theme.accent}`
          }}
        >
          <Card.Body>
            <Card.Title style={{ color: theme.accent }}>
              Change Password
            </Card.Title>

            {message && password !== confirmPassword && (
              <Alert
                variant='danger'
                dismissible
                onClose={() => setMessage(null)}
              >
                {message}
              </Alert>
            )}
            {message && password === confirmPassword && password !== '' && (
              <Alert
                variant='success'
                dismissible
                onClose={() => setMessage(null)}
              >
                {message}
              </Alert>
            )}
            {message && password === '' && (
              <Alert
                variant='warning'
                dismissible
                onClose={() => setMessage(null)}
              >
                {message}
              </Alert>
            )}

            {loading ? (
              <Loader />
            ) : error ? (
              <Alert variant='danger'>{error}</Alert>
            ) : (
              <Form onSubmit={submitHandler}>
                <Form.Group controlId='password' className='mb-3'>
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type='password'
                    placeholder='Enter new password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Group>

                <Form.Group controlId='confirmPassword' className='mb-3'>
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type='password'
                    placeholder='Confirm new password'
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </Form.Group>

                <Button
                  type='submit'
                  variant='outline-primary'
                  style={{
                    borderColor: theme.accent,
                    color: theme.accent,
                    backgroundColor: 'transparent'
                  }}
                >
                  Update Password
                </Button>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Right column — preferences */}
      <Col md={4}>
        <Card
          style={{
            backgroundColor: theme.cardBackground || theme.background,
            border: `1px solid ${theme.accent}`
          }}
        >
          <Card.Body>
            <Card.Title style={{ color: theme.accent }}>Preferences</Card.Title>
            <Form>
              <Form.Group controlId='formBasicCheckbox1' className='mb-2'>
                <Form.Check type='checkbox' label='QReceipt special offers' />
              </Form.Group>
              <Form.Group controlId='formBasicCheckbox2'>
                <Form.Check type='checkbox' label='Info and email' />
              </Form.Group>
            </Form>
          </Card.Body>
        </Card>

        <Card
          className='mt-3'
          style={{
            backgroundColor: theme.cardBackground || theme.background,
            border: `1px solid ${theme.accent}`
          }}
        >
          <Card.Body>
            <Card.Title style={{ color: theme.accent }}>Premium</Card.Title>
            <p style={{ fontSize: '0.875rem' }}>
              Premium features coming soon. Upgrade to unlock advanced receipt
              management and analytics.
            </p>
            <Button
              variant='outline-primary'
              style={{
                borderColor: theme.accent,
                color: theme.accent,
                backgroundColor: 'transparent'
              }}
              onClick={() => navigate('/premium')}
            >
              Upgrade — Coming Soon
            </Button>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default ProfileScreen
